import type { PeriodPreset, PeriodSelection } from './period-presets';

const PERIOD_SELECTION_STORAGE_KEY = 'vertice:period-selection';
const DEFAULT_PERIOD_PRESET: PeriodPreset = 'current_month';
const PERIOD_PRESETS: PeriodPreset[] = ['current_month', 'last_30', 'last_90', 'last_180', 'custom'];

export interface StoredPeriodSelection {
  preset: PeriodPreset;
  from?: string | null;
  to?: string | null;
}

function isPeriodPreset(value: string | null | undefined): value is PeriodPreset {
  return PERIOD_PRESETS.includes(value as PeriodPreset);
}

function formatDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function readStoredPeriodSelection(): StoredPeriodSelection | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(PERIOD_SELECTION_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredPeriodSelection>;
    if (!isPeriodPreset(parsed.preset)) return null;

    return {
      preset: parsed.preset,
      from: typeof parsed.from === 'string' ? parsed.from : null,
      to: typeof parsed.to === 'string' ? parsed.to : null,
    };
  } catch {
    return null;
  }
}

export function writeStoredPeriodSelection(selection: PeriodSelection | StoredPeriodSelection): void {
  if (typeof window === 'undefined') return;

  const next: StoredPeriodSelection =
    selection.preset === 'custom'
      ? {
          preset: 'custom',
          from: selection.from instanceof Date ? formatDateParam(selection.from) : selection.from,
          to: selection.to instanceof Date ? formatDateParam(selection.to) : selection.to,
        }
      : { preset: selection.preset };

  window.localStorage.setItem(PERIOD_SELECTION_STORAGE_KEY, JSON.stringify(next));
}

export function resolvePeriodSelectionParams(searchParams: URLSearchParams): StoredPeriodSelection {
  const queryPreset = searchParams.get('period');
  if (isPeriodPreset(queryPreset)) {
    return {
      preset: queryPreset,
      from: queryPreset === 'custom' ? searchParams.get('from') : null,
      to: queryPreset === 'custom' ? searchParams.get('to') : null,
    };
  }

  return readStoredPeriodSelection() ?? { preset: DEFAULT_PERIOD_PRESET };
}
